import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

// Get publishable key from environment
const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

interface GuaranteeCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  guaranteeAmount: number; // In dollars
  getToken: () => Promise<string | null>;
}

function CardForm({
  guaranteeAmount,
  onSuccess,
  onClose,
  getToken,
}: {
  guaranteeAmount: number;
  onSuccess: () => void;
  onClose: () => void;
  getToken: () => Promise<string | null>;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const token = await getToken();

      // 1. Create a SetupIntent
      const setupResponse = await fetch('/api/stripe/setup-intent', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!setupResponse.ok) {
        const errorData = await setupResponse.json();
        throw new Error(errorData.error || 'Failed to create setup intent');
      }

      const { clientSecret } = await setupResponse.json();

      // 2. Confirm the SetupIntent with card details
      const { setupIntent, error: stripeError } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (stripeError) {
        throw new Error(stripeError.message || 'Card setup failed');
      }

      if (!setupIntent?.payment_method) {
        throw new Error('No payment method returned');
      }

      // 3. Set this as the default payment method
      const setDefaultResponse = await fetch('/api/stripe/set-default-payment-method', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentMethodId: setupIntent.payment_method,
        }),
      });

      if (!setDefaultResponse.ok) {
        throw new Error('Failed to save payment method');
      }

      onSuccess();
    } catch (err) {
      console.error('Error setting up card:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
      <div className="p-4 rounded-xl bg-surface border border-line">
        <p className="type-body-sm text-text-dim mb-3">
          Your card will be authorized for <span className="text-text">${guaranteeAmount.toFixed(2)}</span> when you publish this event.
          This hold will only be charged if ticket sales don't cover the model's guarantee.
        </p>
        <p className="type-caption text-text-faint">
          The hold is released automatically after the event if not needed.
        </p>
      </div>

      <div>
        <label className="label-mono mb-2 block">Card Details</label>
        <div className="p-4 rounded-lg bg-white border border-line">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#1a1a1a',
                  '::placeholder': {
                    color: '#9ca3af',
                  },
                },
                invalid: {
                  color: '#ef4444',
                },
              },
            }}
            onChange={(event) => {
              setCardComplete(event.complete);
              if (event.error) {
                setError(event.error.message);
              } else {
                setError(null);
              }
            }}
          />
        </div>
      </div>

      {error && (
        <div className="type-validation-message" role="alert">
          {error}
        </div>
      )}

      <div className="flex items-center gap-3 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="btn flex-1"
          disabled={isProcessing}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || !cardComplete || isProcessing}
          className="btn btn-accent flex-1"
        >
          {isProcessing ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Saving...
            </span>
          ) : (
            'Save Card'
          )}
        </button>
      </div>
    </form>
  );
}

export function GuaranteeCardModal({
  isOpen,
  onClose,
  onSuccess,
  guaranteeAmount,
  getToken,
}: GuaranteeCardModalProps) {
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(onClose, 200);
  };

  if (!isOpen) return null;

  if (!stripePromise) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
        <div className="relative bg-panel rounded-2xl shadow-xl w-full max-w-md p-6">
          <div className="text-center text-text-dim">
            <p>Stripe is not configured.</p>
            <p className="type-body-sm mt-2">Please add VITE_STRIPE_PUBLISHABLE_KEY to your environment.</p>
            <button onClick={handleClose} className="btn mt-4">Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-200 ${isClosing ? 'opacity-0' : 'opacity-100'}`}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      {/* Modal */}
      <div className={`relative bg-panel rounded-2xl shadow-xl w-full max-w-md p-6 transition-transform duration-200 ${isClosing ? 'scale-95' : 'scale-100'}`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="type-title-card text-text">Add Payment Method</h2>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-lg hover:bg-surface flex items-center justify-center text-text-dim hover:text-text transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Stripe Elements */}
        <Elements stripe={stripePromise}>
          <CardForm
            guaranteeAmount={guaranteeAmount}
            onSuccess={onSuccess}
            onClose={handleClose}
            getToken={getToken}
          />
        </Elements>

        {/* Stripe badge */}
        <div className="type-caption mt-5 pt-4 border-t border-line flex items-center justify-center gap-2 text-text-faint">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          Secured by Stripe
        </div>
      </div>
    </div>
  );
}

export interface PaymentMethodInfo {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

// Hook to check if user has a payment method
export function usePaymentMethodStatus(getToken: () => Promise<string | null>) {
  const [hasPaymentMethod, setHasPaymentMethod] = useState<boolean | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkStatus = async () => {
    try {
      const token = await getToken();
      const response = await fetch('/api/stripe/payment-method-status', {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setHasPaymentMethod(data.hasPaymentMethod);
        setPaymentMethod(data.paymentMethod || null);
      }
    } catch (error) {
      console.error('Error checking payment method status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkStatus();
  }, []);

  return { hasPaymentMethod, paymentMethod, isLoading, refresh: checkStatus };
}
